import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server, RefreshCw, ShoppingCart, Package, ArrowLeft, RotateCcw } from 'lucide-react';
import { listProducts } from '../services/productService';
import { getSpacesTree } from '../services/spaceService';
import { db } from '../db/database';

const ConnectServer = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [bcvRateInput, setBcvRateInput] = useState('');

  useEffect(() => {
    loadPendingCount();
    const storedRate = localStorage.getItem('bcvRate');
    if (storedRate) {
      setBcvRateInput(storedRate);
    }
  }, []);

  const loadPendingCount = async () => {
    try {
      const count = await db.orders.where('status').equals('pending').count();
      setPendingCount(count);
    } catch (error) {
      console.error('Error loading pending count:', error);
    }
  };

  const handleGetProducts = async () => {
    try {
      setIsLoading(true);
      setStatus(null);
      
      const data = await listProducts();
      
      if (data && Array.isArray(data)) {
        await db.products.clear();
        const productsToAdd = data.map(p => ({
          id: p.id,
          name: p.name,
          price: parseFloat(p.price) || 0,
          category: p.category || 'General',
          active: p.active !== false ? 1 : 0
        }));
        await db.products.bulkAdd(productsToAdd);
        setStatus({ 
          type: 'success', 
          message: `✓ ${productsToAdd.length} productos sincronizados`, 
          payload: data 
        });
      } else {
        setStatus({ type: 'error', message: 'Formato de respuesta inválido' });
      }
    } catch (e) {
      setStatus({ type: 'error', message: e.message || 'Error al obtener productos' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetBcvRate = () => {
    try {
      const raw = bcvRateInput || '';
      const value = parseFloat(raw.replace(',', '.'));
      if (Number.isNaN(value) || value <= 0) {
        setStatus({ type: 'error', message: 'Tasa BCV inválida, debe ser un número mayor a 0' });
        return;
      }
      localStorage.setItem('bcvRate', String(value));
      setBcvRateInput(String(value));
      setStatus({ type: 'success', message: `✓ Tasa BCV guardada localmente: ${value}` });
    } catch (e) {
      setStatus({ type: 'error', message: e.message || 'Error al guardar la tasa BCV' });
    }
  };

  const handleGetSpaces = async () => {
    try {
      setIsLoading(true);
      setStatus(null);
      
      // Uses /api/spaces endpoints (operators + admins)
      const spacesData = await getSpacesTree();

      if (spacesData && Array.isArray(spacesData)) {
        // Limpiar tablas relacionadas
        if (db.spaces) {
          await db.spaces.clear();
        }
        await db.stations.clear();
        if (db.nodes) {
          await db.nodes.clear();
        }
        if (db.productsByStations) {
          await db.productsByStations.clear();
        }

        const spacesToAdd = [];
        const stationsToAdd = [];
        const nodesToAdd = [];
        const productsByStationsToAdd = [];

        spacesData.forEach((space) => {
          const spaceId = space.id;
          spacesToAdd.push({
            id: space.id,
            name: space.name,
            active: space.status !== false ? 1 : 0,
          });
          const stations = space.stations || [];

          stations.forEach((station) => {
            stationsToAdd.push({
              id: station.id,
              name: station.name,
              active: station.status !== false ? 1 : 0,
              spaceId,
            });

            nodesToAdd.push({
              id: station.id,
              spaceId,
              name: station.name,
              active: station.status !== false ? 1 : 0,
            });

            const products = station.products || [];
            products.forEach((product) => {
              productsByStationsToAdd.push({
                spaceId,
                stationId: station.id,
                productId: product.id,
                name: product.name,
                price: typeof product.assignment_price === 'number' ? product.assignment_price : product.price || 0,
                basePrice: product.price || 0,
                vat: product.vat ?? 0,
                assignmentPrice: product.assignment_price ?? null,
                assignmentVat: product.assignment_vat ?? null,
                active: product.status !== false ? 1 : 0,
              });
            });
          });
        });

        if (db.spaces && spacesToAdd.length > 0) {
          await db.spaces.bulkAdd(spacesToAdd);
        }
        if (stationsToAdd.length > 0) {
          await db.stations.bulkAdd(stationsToAdd);
        }
        if (db.nodes && nodesToAdd.length > 0) {
          await db.nodes.bulkAdd(nodesToAdd);
        }
        if (db.productsByStations && productsByStationsToAdd.length > 0) {
          await db.productsByStations.bulkAdd(productsByStationsToAdd);
        }

        setStatus({
          type: 'success',
          message: `✓ ${spacesToAdd.length} espacios, ${stationsToAdd.length} estaciones y ${productsByStationsToAdd.length} productos por estación sincronizados`,
          payload: spacesData,
        });
      } else {
        setStatus({ type: 'error', message: 'Formato de respuesta inválido' });
      }
    } catch (e) {
      setStatus({ type: 'error', message: e.message || 'Error al obtener espacios' });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-gray-900 pb-32">
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-4 sticky top-0 z-10 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Server size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Conectarse a servidor</h1>
            <p className="text-sm text-white/80">Configura y prueba la sincronización</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button onClick={handleGetSpaces} className="card p-5 text-left active:scale-95 transition">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mb-3">
              <RefreshCw size={24} className="text-white" />
            </div>
            <h3 className="text-white font-semibold">Obtener espacio</h3>
            <p className="text-gray-400 text-sm">GET api/spaces</p>
          </button>

          <button onClick={handleGetProducts} className="card p-5 text-left active:scale-95 transition">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mb-3">
              <Package size={24} className="text-white" />
            </div>
            <h3 className="text-white font-semibold">Obtener productos</h3>
            <p className="text-gray-400 text-sm">GET api/products</p>
          </button>

          <div className="card p-5 text-left active:scale-95 transition">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mb-3">
              <Server size={24} className="text-white" />
            </div>
            <h3 className="text-white font-semibold mb-1">Seleccionar tasa BCV</h3>
            <p className="text-gray-400 text-sm mb-3">Configura la tasa BCV local para el cálculo en Bs</p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                min="0"
                value={bcvRateInput}
                onChange={(e) => setBcvRateInput(e.target.value)}
                className="input-field flex-1"
                placeholder="Ej: 40.50"
              />
              <button
                type="button"
                onClick={handleSetBcvRate}
                className="px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium"
              >
                Guardar
              </button>
            </div>
          </div>

          <button onClick={() => navigate('/orders')} className="card p-5 text-left active:scale-95 transition relative">
            {pendingCount > 0 && (
              <div className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">{pendingCount}</span>
              </div>
            )}
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mb-3">
              <ShoppingCart size={24} className="text-white" />
            </div>
            <h3 className="text-white font-semibold">Sincronizar órdenes</h3>
            <p className="text-gray-400 text-sm">
              {pendingCount > 0 ? `${pendingCount} pendiente${pendingCount !== 1 ? 's' : ''}` : 'Sin órdenes pendientes'}
            </p>
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3">
          <button
            onClick={() => navigate('/orders-reversed')}
            className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
          >
            <RotateCcw size={16} />
            <span>Ver órdenes reversadas</span>
          </button>
        </div>

        {status && (
          <div className={`p-4 rounded-lg border ${
            status.type === 'success' ? 'bg-green-500/10 border-green-500 text-green-300' :
            status.type === 'error' ? 'bg-red-500/10 border-red-500 text-red-300' :
            'bg-blue-500/10 border-blue-500 text-blue-300'
          }`}>
            <p className="font-semibold mb-1">{status.message}</p>
            {status.payload && (
              <pre className="text-xs whitespace-pre-wrap text-gray-300 overflow-auto max-h-64">{typeof status.payload === 'string' ? status.payload : JSON.stringify(status.payload, null, 2)}</pre>
            )}
          </div>
        )}

          <button
            onClick={() => navigate('/stations')}
            className="btn-secondary w-full flex items-center justify-center gap-2"
          >
            <ArrowLeft size={18} />
            <span>Volver</span>
          </button>
        </div>
        {isLoading && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
            <div className="card p-6 text-center">
              <div className="w-12 h-12 rounded-full border-4 border-primary-500 border-t-transparent animate-spin mx-auto mb-4"></div>
              <p className="text-white">Procesando...</p>
            </div>
          </div>
        )}
      </div>
 
  );
};

export default ConnectServer;
